// TRADING v2 — the pieces (UI rebuild batch 5). The rules and words are today's (screens/TradingReport.tsx), copied:
// AED and INR are never summed; trading margin and cheque fees are kept apart; a margin the engine did not realise is
// "—", never 0.00; "incl. short cover" under a margin a buy realised; the mix in the customer's words; "(own trades)"
// beside the legacy bucket; bps of the money that moved. What moved: one analysis at a time, a book's run by day /
// week / month / year behind its row, the rules behind "Basis & supporting detail".
import { useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../../lib/api';
import type { TradingMixRow, TradingPartyRow, TradingSeriesRow } from '../../../lib/api';
import { readState } from '../../../lib/read-state';
import { booksClock, displayAsset, fmtAmount, fmtDay, fmtMonth, fmtRate } from '../../../lib/format';
import { partyName } from '../../../lib/naming';
import { DetailSheet, EmptyState, ReadFailed, SkeletonRows, type RegisterColumn } from '../kit';

export type Bucket = 'day' | 'week' | 'month' | 'year' | 'all';
export type Analysis = 'cost' | 'mix' | 'party';

export const ANALYSES: ReadonlyArray<readonly [Analysis, string]> = [
  // "By book": the long name cut off in the phone's select (Screens' audit, 27 Oct 2026); the table under it keeps it.
  ['cost', 'By book'],
  ['mix', 'Trade-type mix'],
  ['party', 'By party'],
];

/** Margin in basis points of the money that moved — comparable across corridors (30 AED and 1,900 INR are not). */
export function marginBps(margin: string | null, volume: string): string {
  if (margin === null) return '—';
  const m = Number(margin), v = Number(volume);
  if (!v) return '—';
  return String(Number(((m / v) * 10000).toFixed(1)));   // one place, trailing zero dropped: 34, 12.5
}

export function bucketLabel(p: string | null, bucket: Bucket): string {
  if (!p) return 'All time';
  if (bucket === 'day')   return fmtDay(p, true);
  if (bucket === 'week')  return `Week of ${fmtDay(p)}`;
  if (bucket === 'month') return fmtMonth(p);
  if (bucket === 'year')  return String(booksClock(p)?.y ?? p);
  return p;
}

/** The mix rows in the customer's words: the engine's desk_sell is the customer buying. */
const CLASS_WORDS: Record<string, string> = {
  desk_sell: 'Customer bought',
  desk_buy:  'Customer sold',
  delivery:  'Cheque (fee)',
};
const classWords = (m: TradingMixRow) => CLASS_WORDS[m.trade_class] ?? m.trade_class;

/** What Inventory says of a book's cost when it is an assumption or missing — "Provisional", "Assumed at peg", "No cost set"
 *  (its BASIS_BADGE) — and null when the cost is measured or the inventory read has not answered. */
export type BasisOf = (asset: string, ccy: string) => string | null;

/** A margin: "—" when the engine realised none, the sign's colour, and the short-cover flag under it. A margin measured
 *  against an assumed or missing cost (Inventory's basis, `basis`) is neutral — an estimate is never green or red — and
 *  says so under it, the why one tap away on Inventory (Astra, 1 Nov 2026: the INR margin was green here and Provisional
 *  there). */
export function Margin({ value, ccy, shortCover, unit = false, basis = null }: {
  value: string | number | null; ccy: string; shortCover?: string | null; unit?: boolean; basis?: string | null;
}) {
  if (value === null) return <span className="text-tg-hint">—</span>;
  const n = Number(value);
  return (
    // A block, right-aligned: the figure and its unit on one line, the marks each on their own line under it — never running into
    // the unit or into the next column (Astra, 1 Nov 2026).
    <span className={`inline-block whitespace-nowrap text-right font-mono tabular ${basis ? '' : n < 0 ? 'text-neg' : n > 0 ? 'text-pos' : ''}`}>
      <span className="block">{fmtAmount(value, ccy)}{unit && <span className="ml-1 font-sans text-meta font-normal text-tg-hint">{displayAsset(ccy)}</span>}</span>
      {shortCover && Number(shortCover) !== 0 && (
        <span className="block text-right font-sans text-meta font-normal leading-tight text-tg-hint" title="realised by a buy covering a short position">
          incl. short cover
        </span>
      )}
      {/* The basis as a marked state, not plain coloured words (the audit, 1 Nov 2026). */}
      {basis && <Link to="/inventory" className="tap-44 pill mt-1 bg-warn-bg font-sans font-normal text-warn hover:underline">{basis} →</Link>}
    </span>
  );
}

const pct = (v: number | null) => (v === null ? '—' : `${v.toFixed(1)}%`);
const dealCount = (n: string) => Number(n).toLocaleString('en-US');
const deals = (n: string) => `${dealCount(n)} ${Number(n) === 1 ? 'deal' : 'deals'}`;
export const bookName = (asset: string, ccy: string) => `${displayAsset(asset)} · ${displayAsset(ccy)}`;
export const bookKey = (r: { inv_asset: string; quote_ccy: string }) => `${r.inv_asset}|${r.quote_ccy}`;
/** One rate a cell (the audit, 1 Nov 2026: "Cost / sell" held two rates in one): the figure, "—" when there is none. */
const rateCell = (v: string | number | null | undefined, ccy: string) => (v == null ? <span className="text-tg-hint">—</span> : <span className="whitespace-nowrap font-mono tabular">{fmtRate(v, ccy)}</span>);

/** A phone card's labelled line: the label left and muted, the value right — what the laptop shows as a column. */
const CardLine = ({ label, children }: { label: string; children: ReactNode }) => (
  <span className="flex items-start justify-between gap-3 text-body font-normal"><span className="pt-0.5 text-meta text-tg-hint">{label}</span><span className="text-right">{children}</span></span>
);

// ── the three analyses: one row spec each, a table from lg and a card per row on the phone ────────────────────────

/** `period`: the head's words, or — grouped by day / week / month / year — each row's own bucket. */
/** `period`: a function gives each row its own bucket (grouped by day / week / month / year); a string is the head's period,
 *  said once in the head and not again on every row (Astra, 1 Nov 2026). */
export function costColumns(period: string | ((r: TradingSeriesRow) => string), basisOf: BasisOf = () => null): RegisterColumn<TradingSeriesRow>[] {
  return [
    // The book's name is two short words: a narrow column leaves the figures room to keep each amount beside its unit (Astra, 1 Nov 2026).
    { key: 'book', head: 'Inventory book', cell: (r) => (
      <><span className="block font-medium">{bookName(r.inv_asset, r.quote_ccy)}</span>{typeof period !== 'string' && <span className="mt-0.5 block text-meta text-tg-hint">{period(r)}</span>}</>
    ) },
    // Deals and margin bps, back in the main table (the old screen's columns; his "restore most the lost things", 1 Nov 2026).
    // FIXED SEATS FOR THE FIGURES (the audit at 1024, 1 Nov 2026: Quantity, Volume, Cost / sell and Margin ran together);
    // the register draws cards below 1280 (Trading.tsx, tableFrom="xl").
    { key: 'deals', head: 'Deals', align: 'right', width: 'w-[4.5rem]', cell: (r) => <span className="font-mono tabular">{dealCount(r.deals)}</span> },
    { key: 'qty', head: 'Quantity', align: 'right', width: 'w-[10rem]', cell: (r) => <span className="whitespace-nowrap font-mono tabular">{fmtAmount(r.volume_qty, r.inv_asset)} <span className="font-sans text-meta text-tg-hint">{displayAsset(r.inv_asset)}</span></span> },
    { key: 'vol', head: 'Volume', align: 'right', width: 'w-[10.5rem]', cell: (r) => <span className="whitespace-nowrap font-mono tabular">{fmtAmount(r.volume_quote, r.quote_ccy)} <span className="font-sans text-meta text-tg-hint">{displayAsset(r.quote_ccy)}</span></span> },
    { key: 'cost', head: 'Avg cost', align: 'right', width: 'w-[6.5rem]', cell: (r) => rateCell(r.avg_cost_rate, r.quote_ccy) },
    { key: 'sell', head: 'Avg sell', align: 'right', width: 'w-[6.5rem]', cell: (r) => rateCell(r.avg_sell_rate, r.quote_ccy) },
    { key: 'margin', head: 'Margin', align: 'right', width: 'w-[10rem]', cell: (r) => <Margin value={r.trading_margin} ccy={r.quote_ccy} shortCover={r.short_cover_margin} unit basis={basisOf(r.inv_asset, r.quote_ccy)} /> },
    { key: 'bps', head: 'Margin bps', align: 'right', width: 'w-[6rem]', cell: (r) => <span className="font-mono tabular text-tg-hint">{marginBps(r.trading_margin, r.volume_quote)}</span> },
  ];
}
export const costCard = (period: string | ((r: TradingSeriesRow) => string), basisOf: BasisOf = () => null) => (r: TradingSeriesRow) => ({
  // THE PHONE KEEPS THE COLUMNS AS LABELLED LINES (the audit, 1 Nov 2026: the cards dropped their labels, Deals and bps).
  title: (
    <>
      {/* the card's full width: every value on one right edge (Astra, 1 Nov 2026) */}
      <span aria-hidden className="block h-0 w-screen max-w-full" />
      <span className="block">{bookName(r.inv_asset, r.quote_ccy)}</span>
      {typeof period !== 'string' && <span className="mt-0.5 block text-meta font-normal text-tg-hint">{period(r)}</span>}
      <span className="mt-2 block space-y-1">
        <CardLine label="Margin"><Margin value={r.trading_margin} ccy={r.quote_ccy} shortCover={r.short_cover_margin} unit basis={basisOf(r.inv_asset, r.quote_ccy)} /></CardLine>
        <CardLine label="Deals"><span className="font-mono tabular">{dealCount(r.deals)}</span></CardLine>
        <CardLine label="Quantity"><span className="whitespace-nowrap font-mono tabular">{fmtAmount(r.volume_qty, r.inv_asset)}</span> <span className="text-meta text-tg-hint">{displayAsset(r.inv_asset)}</span></CardLine>
        <CardLine label="Volume"><span className="whitespace-nowrap font-mono tabular">{fmtAmount(r.volume_quote, r.quote_ccy)}</span> <span className="text-meta text-tg-hint">{displayAsset(r.quote_ccy)}</span></CardLine>
        <CardLine label="Avg cost">{rateCell(r.avg_cost_rate, r.quote_ccy)}</CardLine>
        <CardLine label="Avg sell">{rateCell(r.avg_sell_rate, r.quote_ccy)}</CardLine>
        <CardLine label="Margin bps"><span className="font-mono tabular">{marginBps(r.trading_margin, r.volume_quote)}</span></CardLine>
      </span>
    </>
  ),
});

export const MIX_COLUMNS: RegisterColumn<TradingMixRow>[] = [
  { key: 'type', head: 'Type', width: 'w-[24%]', cell: (m) => <span className="font-medium">{classWords(m)}</span> },
  { key: 'ccy', head: 'Currency', cell: (m) => <span className="text-tg-hint">{displayAsset(m.quote_ccy)}</span> },
  { key: 'deals', head: 'Deals', align: 'right', cell: (m) => <span className="font-mono tabular">{dealCount(m.deals)}</span> },
  { key: 'vol', head: '% of volume', align: 'right', cell: (m) => <span className="font-mono tabular text-tg-hint">{pct(m.share_of_volume_pct)}</span> },
  { key: 'mgn', head: '% of margin', align: 'right', cell: (m) => <span className="font-mono tabular text-tg-hint">{pct(m.share_of_margin_pct)}</span> },
  { key: 'margin', head: 'Margin', align: 'right', cell: (m) => <Margin value={m.margin} ccy={m.quote_ccy} unit /> },
];
export const mixCard = (m: TradingMixRow) => ({
  title: (
    <>
      <span className="block">{classWords(m)} <span className="font-normal text-tg-hint">· {displayAsset(m.quote_ccy)}</span></span>
      <span className="mt-2 block space-y-1">
        <CardLine label="Deals"><span className="font-mono tabular">{dealCount(m.deals)}</span></CardLine>
        <CardLine label="% of volume"><span className="font-mono tabular">{pct(m.share_of_volume_pct)}</span></CardLine>
        <CardLine label="% of margin"><span className="font-mono tabular">{pct(m.share_of_margin_pct)}</span></CardLine>
      </span>
    </>
  ),
  figure: <Margin value={m.margin} ccy={m.quote_ccy} unit />,
});

const ownTrades = (p: TradingPartyRow) => p.party_slug === 'unnamed-legacy' && <span className="ml-2 text-meta font-normal text-tg-hint">(own trades)</span>;
/** A party's margin is measured against the same books: when any book in its quote currency rests on an assumed or missing
 *  cost, its margin is an estimate too — neutral and marked, as the summary says (Astra, 1 Nov 2026). */
export type BasisOfCcy = (ccy: string) => string | null;
export const partyColumns = (basisCcy: BasisOfCcy = () => null): RegisterColumn<TradingPartyRow>[] => [
  { key: 'party', head: 'Party', width: 'w-[36%]', cell: (p) => <span className="who">{partyName({ slug: p.party_slug })}{ownTrades(p)}</span> },
  { key: 'ccy', head: 'Currency', cell: (p) => <span className="text-tg-hint">{displayAsset(p.quote_ccy)}</span> },
  { key: 'deals', head: 'Deals', align: 'right', cell: (p) => <span className="font-mono tabular">{dealCount(p.deals)}</span> },
  { key: 'vol', head: 'Volume', align: 'right', cell: (p) => <span className="font-mono tabular">{fmtAmount(p.volume_quote, p.quote_ccy)}</span> },
  { key: 'margin', head: 'Margin', align: 'right', cell: (p) => <Margin value={p.margin} ccy={p.quote_ccy} unit basis={basisCcy(p.quote_ccy)} /> },
];
export const PARTY_COLUMNS = partyColumns();
export const partyCard = (basisCcy: BasisOfCcy = () => null) => (p: TradingPartyRow) => ({
  title: (
    <>
      <span className="who block">{partyName({ slug: p.party_slug })}{ownTrades(p)}</span>
      <span className="mt-2 block space-y-1">
        <CardLine label="Deals"><span className="font-mono tabular">{dealCount(p.deals)}</span></CardLine>
        <CardLine label="Volume"><span className="whitespace-nowrap font-mono tabular">{fmtAmount(p.volume_quote, p.quote_ccy)}</span> <span className="text-meta text-tg-hint">{displayAsset(p.quote_ccy)}</span></CardLine>
      </span>
    </>
  ),
  figure: <Margin value={p.margin} ccy={p.quote_ccy} unit basis={basisCcy(p.quote_ccy)} />,
});

// ── the strip: the answer for the period, never across currencies ────────────────────────────────────────────

/** ONE BOOK in the period: its average cost, average sell and realised margin (Astra's three). MORE THAN ONE: the
 *  realised margin per quote currency — an average cost across books, or a margin across AED and INR, would be an
 *  invented rate. A currency with no realised margin reads "—", never 0. */
export function stripItems(series: TradingSeriesRow[], basisOf: BasisOf = () => null) {
  const only = series.length === 1 ? series[0] : undefined;
  if (only) {
    return [
      { label: 'Avg cost', value: <span>{fmtRate(only.avg_cost_rate, only.quote_ccy)}</span>, sub: `${displayAsset(only.quote_ccy)} per ${displayAsset(only.inv_asset)}` },
      { label: 'Avg sell', value: <span>{fmtRate(only.avg_sell_rate, only.quote_ccy)}</span>, sub: `${displayAsset(only.quote_ccy)} per ${displayAsset(only.inv_asset)}` },
      { label: 'Margin', value: <Margin value={only.trading_margin} ccy={only.quote_ccy} shortCover={only.short_cover_margin} unit basis={basisOf(only.inv_asset, only.quote_ccy)} />, sub: deals(only.deals) },
    ];
  }
  const byCcy = new Map<string, { margin: number | null; deals: number; short: boolean; basis: string | null }>();
  for (const r of series) {
    const t = byCcy.get(r.quote_ccy) ?? { margin: null, deals: 0, short: false, basis: null };
    t.basis = t.basis ?? (r.trading_margin !== null ? basisOf(r.inv_asset, r.quote_ccy) : null);
    if (r.trading_margin !== null) t.margin = (t.margin ?? 0) + Number(r.trading_margin);
    t.deals += Number(r.deals);
    if (r.short_cover_margin && Number(r.short_cover_margin) !== 0) t.short = true;
    byCcy.set(r.quote_ccy, t);
  }
  return [...byCcy].slice(0, 4).map(([ccy, t]) => ({
    label: `Margin · ${displayAsset(ccy)}`,
    value: <Margin value={t.margin === null ? null : t.margin.toFixed(2)} ccy={ccy} shortCover={t.short ? '1' : null} unit basis={t.basis} />,
    sub: deals(String(t.deals)),
  }));
}

// ── one book opened: its run over the period, grouped ───────────────────────────────────────────────────────────

const GROUPS: ReadonlyArray<readonly [Exclude<Bucket, 'all'>, string]> = [['day', 'By day'], ['week', 'By week'], ['month', 'By month'], ['year', 'By year']];

/** A BOOK'S RUN: the same report, grouped by day / week / month / year (today's "Group by"), for this book and this
 *  period — each row its deals, volume, cost / sell, margin and bps. */
export function BookDetail({ book, period, from, to, party, onClose }: {
  book: string; period: string; from?: string; to?: string; party: string; onClose: () => void;
}) {
  const [asset, ccy] = book.split('|') as [string, string];
  const [bucket, setBucket] = useState<Exclude<Bucket, 'all'>>('day');
  const q = useQuery({
    queryKey: ['trading-report', bucket, party, from ?? '', to ?? ''],
    queryFn: () => api.tradingReport({ bucket, from, to, ...(party ? { party } : {}) }),
  });
  const rs = readState(q);
  const rows = (q.data?.series ?? []).filter((r) => r.inv_asset === asset && r.quote_ccy === ccy);
  return (
    <DetailSheet title={bookName(asset, ccy)} onClose={onClose}>
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <span className="text-meta text-tg-hint">{period}</span>
        <label>
          <span className="sr-only">Group by</span>
          <select className="select" value={bucket} onChange={(e) => setBucket(e.target.value as Exclude<Bucket, 'all'>)}>
            {GROUPS.map(([k, label]) => <option key={k} value={k}>{label}</option>)}
          </select>
        </label>
      </div>
      {rs === 'failed' && <ReadFailed what="this book" error={q.error} onRetry={() => { void q.refetch(); }} className="mb-3" />}
      {rs === 'waiting' && !q.data && <SkeletonRows rows={4} what="this book" />}
      {rows.length === 0 && rs === 'known' && <EmptyState>Nothing in this period.</EmptyState>}
      {rows.length > 0 && (
        <ul className="rounded-[10px] border border-stroke">
          {rows.map((r, i) => (
            <li key={`${r.period ?? 'all'}-${i}`} className="border-b border-stroke-soft px-4 py-3 last:border-b-0">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <div className="text-body font-medium">{bucketLabel(r.period, bucket)}</div>
                  <div className="mt-0.5 text-meta text-tg-hint">{deals(r.deals)}</div>
                </div>
                <div className="shrink-0 text-right text-body">
                  <Margin value={r.trading_margin} ccy={r.quote_ccy} shortCover={r.short_cover_margin} />
                  {marginBps(r.trading_margin, r.volume_quote) !== '—' && (
                    <div className="font-mono tabular text-meta text-tg-hint">{marginBps(r.trading_margin, r.volume_quote)}<span className="font-sans"> bps</span></div>
                  )}
                </div>
              </div>
              <div className="mt-2 space-y-1">
                <CardLine label="Volume"><span className="whitespace-nowrap font-mono tabular">{fmtAmount(r.volume_quote, r.quote_ccy)}</span> <span className="text-meta text-tg-hint">{displayAsset(r.quote_ccy)}</span></CardLine>
                <CardLine label="Avg cost">{rateCell(r.avg_cost_rate, r.quote_ccy)}</CardLine>
                <CardLine label="Avg sell">{rateCell(r.avg_sell_rate, r.quote_ccy)}</CardLine>
              </div>
            </li>
          ))}
        </ul>
      )}
    </DetailSheet>
  );
}
